/**
 * Step extraction from WbAgent turns.
 *
 * Converts turn-level data (messages, tool calls) into a flat list of
 * ProgressStep objects suitable for rendering in any UI framework.
 *
 * All extraction relies exclusively on the typed structured fields
 * (role, content, tool_calls) on MessageRecord. The opaque `message`
 * dict is never inspected.
 */

import type {TurnResponse} from './WbAgentClient';

// =============================================================================
// Types
// =============================================================================

export type ProgressStep = {
  type: 'tool_call' | 'message' | 'thinking';
  label: string;
  detail?: string;
  timestamp?: string;
  /** Tool call ID — present only for tool_call steps. */
  callId?: string;
  /** Tool response text. null = pending, string = completed. Only for tool_call steps. */
  response?: string | null;
};

// =============================================================================
// Formatting helpers
// =============================================================================

/**
 * Best-effort one-liner from tool call arguments.
 *
 * Tool arguments are inherently dynamic (`Record<string, unknown>`) — their
 * shape varies per tool and there is no typed alternative.  This helper scans
 * for the first short string value to use as a human-readable summary.
 */
export const summarizeToolArgs = (
  args: Record<string, unknown>
): string | undefined => {
  for (const val of Object.values(args)) {
    if (typeof val === 'string' && val.length > 0) {
      return val;
    }
  }
  return undefined;
};

/**
 * Render tool output as a displayable string.
 *
 * `ToolCallResponseRecord.output` is typed as `unknown` — we handle the
 * two unambiguous cases (string, nullish) and fall back to JSON for anything
 * else.  No heuristic structure detection.
 */
const formatToolOutput = (output: unknown): string => {
  if (typeof output === 'string') {
    return output;
  }
  if (output == null) {
    return '';
  }
  return JSON.stringify(output, null, 2);
};

// =============================================================================
// Step extraction
// =============================================================================

/**
 * Extract progress steps from a turn.
 *
 * Tool call steps are sourced from `turn.tool_calls` (the environment-level
 * execution log) because invocations and responses are recorded in real time
 * with distinct timestamps.  This lets the frontend show tool calls as
 * "pending" while they execute.
 *
 * Reasoning and assistant-message steps come from the message stream,
 * using only the typed structured fields (role, content).
 */
export const extractSteps = (turn: TurnResponse): ProgressStep[] => {
  const steps: ProgressStep[] = [];
  const allMessages = turn.messages ?? [];
  const turnToolCalls = turn.tool_calls ?? [];

  // Build response map from turn-level tool call records
  const responseMap = new Map<string, string>();
  for (const tc of turnToolCalls) {
    if (tc.type === 'response') {
      responseMap.set(tc.call_id, formatToolOutput(tc.output));
    }
  }

  // Build tool call steps from turn-level invocations
  for (const tc of turnToolCalls) {
    if (tc.type === 'invocation') {
      const args = tc.arguments as Record<string, unknown>;
      steps.push({
        type: 'tool_call',
        label: tc.name,
        detail: summarizeToolArgs(args),
        timestamp: tc.timestamp,
        callId: tc.call_id,
        response: responseMap.has(tc.call_id)
          ? responseMap.get(tc.call_id)!
          : null,
      });
    }
  }

  // Extract reasoning and message-content steps from the message stream.
  // Only typed structured fields are used — messages without a role are skipped.
  for (const msg of allMessages) {
    if (msg.role == null) {
      continue;
    }
    if (msg.role === 'user' || msg.role === 'tool') {
      continue;
    }
    // Skip assistant messages that only carry tool_calls (already handled above)
    if (
      msg.role === 'assistant' &&
      msg.tool_calls &&
      msg.tool_calls.length > 0
    ) {
      continue;
    }
    if (msg.role === 'reasoning') {
      if (msg.content) {
        steps.push({
          type: 'thinking',
          label: msg.content,
          timestamp: msg.timestamp,
        });
      }
      continue;
    }
    if (msg.content) {
      steps.push({
        type: 'message',
        label: msg.content,
        timestamp: msg.timestamp,
      });
    }
  }

  steps.sort((a, b) => (a.timestamp ?? '').localeCompare(b.timestamp ?? ''));
  return steps;
};
